import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../context/PermissionsContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import {
    Plus, Play, Mail, Users, Sparkles, Search, Trash2,
    Activity, Send, Eye, MessageSquare, Clock, CheckCircle, AlertTriangle,
    TrendingUp, BarChart3, ArrowRight, Zap
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
    draft: 'bg-stone-100 text-stone-600',
    active: 'bg-emerald-50 text-emerald-700',
    paused: 'bg-amber-50 text-amber-700',
    completed: 'bg-blue-50 text-blue-700',
};

// 7b-ii — visibility badge (matches the email-template scheme: org=violet, shared=amber, private=stone)
const visibilityBadge = {
    organization: { label: 'Org-wide', cls: 'bg-violet-100 text-violet-700' },
    shared: { label: 'Shared', cls: 'bg-amber-100 text-amber-700' },
    private: { label: 'Private', cls: 'bg-stone-100 text-stone-600' },
};

const eventIcons = {
    sent: { icon: Send, color: 'text-blue-500', bg: 'bg-blue-50' },
    delivered: { icon: CheckCircle, color: 'text-blue-600', bg: 'bg-blue-50' },
    opened: { icon: Eye, color: 'text-emerald-500', bg: 'bg-emerald-50' },
    clicked: { icon: TrendingUp, color: 'text-stone-600', bg: 'bg-stone-100' },
    responded: { icon: MessageSquare, color: 'text-violet-500', bg: 'bg-violet-50' },
    bounced: { icon: AlertTriangle, color: 'text-red-500', bg: 'bg-red-50' },
};

export default function EmailSequences() {
    const { getAuthHeader } = useAuth();
    const { can } = usePermissions();
    const navigate = useNavigate();
    const [sequences, setSequences] = useState([]);
    const [metrics, setMetrics] = useState({});
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [showCreate, setShowCreate] = useState(false);
    const [accounts, setAccounts] = useState([]);
    const [createForm, setCreateForm] = useState({ name: '', account_id: '' });

    useEffect(() => { loadData(); }, []);

    const loadData = async () => {
        try {
            const headers = getAuthHeader();
            const [seqRes, metricsRes, accRes] = await Promise.all([
                axios.get(`${API}/sequences`, { headers }),
                axios.get(`${API}/sequences/metrics/summary`, { headers }),
                axios.get(`${API}/accounts`, { headers }),
            ]);
            setSequences(Array.isArray(seqRes.data) ? seqRes.data : (seqRes.data?.data || []));
            setMetrics(metricsRes.data);
            setAccounts(Array.isArray(accRes.data) ? accRes.data : accRes.data.data || []);
        } catch { toast.error('Failed to load sequences'); }
        finally { setLoading(false); }
    };

    const handleCreate = async () => {
        if (!createForm.name.trim() || !createForm.account_id) {
            return toast.error('Please fill in all fields');
        }
        try {
            const res = await axios.post(`${API}/sequences`, createForm, { headers: getAuthHeader() });
            toast.success('Sequence created');
            setShowCreate(false);
            setCreateForm({ name: '', account_id: '' });
            navigate(`/email-sequences/${res.data.sequence_id}/edit`);
        } catch { toast.error('Failed to create sequence'); }
    };

    const handleDelete = async (seqId) => {
        if (!window.confirm('Delete this sequence?')) return;
        try {
            await axios.delete(`${API}/sequences/${seqId}`, { headers: getAuthHeader() });
            toast.success('Sequence deleted');
            loadData();
        } catch { toast.error('Failed to delete'); }
    };

    const getSeqStats = (seq) => {
        let scheduled = 0, sent = 0, opened = 0, responded = 0, bounced = 0, aiPending = 0;
        for (const c of seq.contacts || []) {
            for (const e of c.emails || []) {
                if (e.status === 'scheduled') scheduled++;
                else if (e.status === 'sent') sent++;
                else if (e.status === 'opened') opened++;
                else if (e.status === 'responded') responded++;
                else if (e.status === 'bounced' || e.status === 'complained') bounced++;
                for (const s of e.ai_suggestions || []) {
                    if (s.status === 'pending') aiPending++;
                }
            }
        }
        return { scheduled, sent, opened, responded, bounced, aiPending, totalEmails: scheduled + sent + opened + responded + bounced };
    };

    const filtered = sequences.filter(s =>
        !search || s.name.toLowerCase().includes(search.toLowerCase()) || s.account_name?.toLowerCase().includes(search.toLowerCase())
    );

    const funnel = metrics.engagement_funnel || [];
    const maxFunnel = Math.max(...funnel.map(f => f.count), 1);
    const recentEvents = metrics.recent_events || [];

    if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-4 border-stone-700 border-t-transparent rounded-full animate-spin" /></div>;

    return (
        <div className="space-y-6 animate-fade-in">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="sequences-title">Email Sequences</h1>
                    <p className="text-sm text-stone-500 mt-1">Create multi-contact email sequences with AI-powered contextual rewriting</p>
                </div>
                <Button onClick={() => setShowCreate(true)} className="bg-stone-900 hover:bg-stone-800" data-testid="new-sequence-btn">
                    <Plus className="w-4 h-4 mr-2" /> New Sequence
                </Button>
            </div>

            {/* KPI Row */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="metrics-bar">
                {[
                    { label: 'Active Sequences', value: metrics.active_sequences || 0, icon: Play, color: 'text-emerald-600', bg: 'bg-emerald-50' },
                    { label: 'Emails Sent', value: metrics.emails_sent || 0, icon: Send, color: 'text-blue-600', bg: 'bg-blue-50' },
                    { label: 'Open Rate', value: `${metrics.open_rate || 0}%`, icon: Eye, color: 'text-stone-700', bg: 'bg-stone-100' },
                    { label: 'Response Rate', value: `${metrics.response_rate || 0}%`, icon: MessageSquare, color: 'text-violet-600', bg: 'bg-violet-50' },
                ].map((m, i) => (
                    <div key={i} className="bg-white rounded-xl border border-stone-200 p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-xs text-stone-400 font-medium mb-1">{m.label}</p>
                                <p className={`text-2xl font-bold ${m.color}`} style={{ fontFamily: 'Manrope, sans-serif' }}>{m.value}</p>
                            </div>
                            <div className={`w-10 h-10 ${m.bg} rounded-xl flex items-center justify-center`}>
                                <m.icon className={`w-5 h-5 ${m.color}`} />
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Analytics Row: Funnel + Recent Activity */}
            {(metrics.total_emails > 0 || recentEvents.length > 0) && (
                <div className="grid grid-cols-1 lg:grid-cols-5 gap-4" data-testid="analytics-row">
                    {/* Engagement Funnel */}
                    <div className="lg:col-span-3 bg-white rounded-xl border border-stone-200 p-5">
                        <div className="flex items-center gap-2 mb-4">
                            <BarChart3 className="w-4 h-4 text-stone-700" />
                            <h3 className="text-sm font-semibold text-stone-700">Engagement Funnel</h3>
                            <span className="text-xs text-stone-400 ml-auto">{metrics.total_emails || 0} total emails</span>
                        </div>
                        <div className="space-y-3">
                            {funnel.map((stage, i) => {
                                const pct = maxFunnel ? Math.round((stage.count / maxFunnel) * 100) : 0;
                                const colors = ['bg-stone-400', 'bg-blue-500', 'bg-emerald-500', 'bg-violet-500'];
                                return (
                                    <div key={stage.stage} className="flex items-center gap-3" data-testid={`funnel-${stage.stage.toLowerCase()}`}>
                                        <span className="text-xs text-stone-500 w-20 text-right font-medium">{stage.stage}</span>
                                        <div className="flex-1 bg-stone-100 rounded-full h-6 overflow-hidden">
                                            <div className={`h-full ${colors[i]} rounded-full transition-all duration-500 flex items-center justify-end pr-2`}
                                                style={{ width: `${Math.max(pct, stage.count > 0 ? 8 : 0)}%` }}>
                                                {stage.count > 0 && <span className="text-xs text-white font-bold">{stage.count}</span>}
                                            </div>
                                        </div>
                                        <span className="text-xs text-stone-400 w-8 text-right">{stage.count}</span>
                                    </div>
                                );
                            })}
                        </div>
                        {/* Secondary stats */}
                        <div className="flex items-center gap-6 mt-4 pt-4 border-t border-stone-100">
                            <div className="text-center">
                                <p className="text-lg font-bold text-stone-700" style={{ fontFamily: 'Manrope, sans-serif' }}>{metrics.total_contacts || 0}</p>
                                <p className="text-xs text-stone-400">Contacts</p>
                            </div>
                            <div className="text-center">
                                <p className="text-lg font-bold text-amber-600" style={{ fontFamily: 'Manrope, sans-serif' }}>{metrics.ai_rewrites_pending || 0}</p>
                                <p className="text-xs text-stone-400">AI Pending</p>
                            </div>
                            <div className="text-center">
                                <p className="text-lg font-bold text-emerald-600" style={{ fontFamily: 'Manrope, sans-serif' }}>{metrics.ai_rewrites_accepted || 0}</p>
                                <p className="text-xs text-stone-400">AI Accepted</p>
                            </div>
                            <div className="text-center">
                                <p className="text-lg font-bold text-red-500" style={{ fontFamily: 'Manrope, sans-serif' }}>{metrics.bounce_rate || 0}%</p>
                                <p className="text-xs text-stone-400">Bounce Rate</p>
                            </div>
                        </div>
                    </div>

                    {/* Recent Activity Feed */}
                    <div className="lg:col-span-2 bg-white rounded-xl border border-stone-200 p-5">
                        <div className="flex items-center gap-2 mb-4">
                            <Activity className="w-4 h-4 text-stone-700" />
                            <h3 className="text-sm font-semibold text-stone-700">Recent Activity</h3>
                        </div>
                        <div className="space-y-2 max-h-56 overflow-y-auto">
                            {recentEvents.length === 0 ? (
                                <p className="text-xs text-stone-400 text-center py-6">No engagement activity yet</p>
                            ) : recentEvents.slice(0, 10).map((ev, i) => {
                                const cfg = eventIcons[ev.event_type] || eventIcons.sent;
                                const Icon = cfg.icon;
                                return (
                                    <div key={i} className="flex items-start gap-2.5 py-1.5" data-testid={`recent-event-${i}`}>
                                        <div className={`w-6 h-6 rounded-full ${cfg.bg} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                                            <Icon className={`w-3 h-3 ${cfg.color}`} />
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <p className="text-xs text-stone-700">
                                                <span className="font-medium">{ev.contact_name}</span>
                                                <span className="text-stone-400"> &middot; </span>
                                                <span className="capitalize">{ev.event_type}</span>
                                            </p>
                                            <p className="text-xs text-stone-400 truncate">{ev.email_subject || ev.sequence_name}</p>
                                        </div>
                                        <span className="text-xs text-stone-300 flex-shrink-0 whitespace-nowrap">
                                            {ev.timestamp ? new Date(ev.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}

            {/* Search */}
            <div className="relative max-w-xs">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search sequences..." className="pl-9" data-testid="sequence-search" />
            </div>

            {/* Sequence Cards */}
            {filtered.length === 0 ? (
                <div className="text-center py-16 border-2 border-dashed border-stone-200 rounded-xl">
                    <Mail className="w-12 h-12 mx-auto text-stone-300 mb-4" />
                    <h3 className="text-lg font-semibold text-stone-700 mb-2">No sequences yet</h3>
                    <p className="text-stone-500 mb-4">Create your first multi-contact email sequence</p>
                    <Button onClick={() => setShowCreate(true)}>Create Sequence</Button>
                </div>
            ) : (
                <div className="space-y-4">
                    {filtered.map(seq => {
                        const stats = getSeqStats(seq);
                        const delivered = stats.sent + stats.opened + stats.responded;
                        const seqOpenRate = delivered ? Math.round((stats.opened + stats.responded) / delivered * 100) : 0;
                        return (
                            <div key={seq.sequence_id} className="bg-white border border-stone-200 rounded-xl p-5 hover:shadow-md transition-shadow" data-testid={`sequence-card-${seq.sequence_id}`}>
                                {/* AI Alert Banner */}
                                {stats.aiPending > 0 && (
                                    <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3" data-testid={`ai-alert-${seq.sequence_id}`}>
                                        <Sparkles className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                                        <div className="flex-1">
                                            <p className="text-sm text-amber-800 font-medium">Cross-contact intelligence active</p>
                                            <p className="text-xs text-amber-600 mt-0.5">AI has suggested {stats.aiPending} contextual rewrite{stats.aiPending > 1 ? 's' : ''} based on engagement signals.</p>
                                        </div>
                                        <Button size="sm" variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-100" onClick={() => navigate(`/email-sequences/${seq.sequence_id}/monitor`)}>
                                            Review
                                        </Button>
                                    </div>
                                )}

                                <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-1">
                                            <h3 className="font-semibold text-stone-900 text-lg">{seq.name}</h3>
                                            <span className={`text-xs px-2.5 py-0.5 rounded-full font-medium capitalize ${statusColors[seq.status] || statusColors.draft}`}>{seq.status}</span>
                                            {(() => {
                                                const b = visibilityBadge[seq.visibility || 'organization'] || visibilityBadge.organization;
                                                return <span className={`text-xs px-2.5 py-0.5 rounded-full font-medium ${b.cls}`}>{b.label}</span>;
                                            })()}
                                        </div>
                                        <div className="flex items-center gap-4 text-xs text-stone-400 mt-1">
                                            <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" />{(seq.contacts || []).length} contacts</span>
                                            <span className="flex items-center gap-1"><Mail className="w-3.5 h-3.5" />{stats.totalEmails} emails</span>
                                            <span>{seq.account_name}</span>
                                            {seq.created_by_name && <span>by {seq.created_by_name}</span>}
                                        </div>
                                    </div>
                                    {can('sequences:delete') && <button onClick={() => handleDelete(seq.sequence_id)} className="p-2 hover:bg-red-50 rounded-lg text-stone-400 hover:text-red-500" data-testid={`delete-sequence-${seq.sequence_id}`}>
                                        <Trash2 className="w-4 h-4" />
                                    </button>}
                                </div>

                                {/* Stats Row with mini funnel */}
                                <div className="grid grid-cols-5 gap-3 mt-4 pt-4 border-t border-stone-100">
                                    {[
                                        { label: 'Scheduled', val: stats.scheduled, icon: Clock, color: 'text-stone-500' },
                                        { label: 'Sent', val: stats.sent, icon: Send, color: 'text-blue-600' },
                                        { label: 'Opened', val: stats.opened, icon: Eye, color: 'text-emerald-600' },
                                        { label: 'Responded', val: stats.responded, icon: MessageSquare, color: 'text-violet-600' },
                                        { label: 'Open Rate', val: `${seqOpenRate}%`, icon: TrendingUp, color: 'text-stone-700' },
                                    ].map((s, i) => (
                                        <div key={i} className="text-center">
                                            <div className="flex items-center justify-center gap-1.5 mb-0.5">
                                                <s.icon className={`w-3.5 h-3.5 ${s.color}`} />
                                                <span className={`text-lg font-bold ${s.color}`}>{s.val}</span>
                                            </div>
                                            <p className="text-xs text-stone-400">{s.label}</p>
                                        </div>
                                    ))}
                                </div>

                                {/* Actions */}
                                <div className="flex items-center gap-2 mt-4 pt-3 border-t border-stone-100">
                                    <button onClick={() => navigate(`/email-sequences/${seq.sequence_id}/edit`)} className="text-sm text-stone-700 hover:text-stone-900 font-medium" data-testid={`edit-seq-${seq.sequence_id}`}>
                                        Edit Sequence
                                    </button>
                                    <Button size="sm" onClick={() => navigate(`/email-sequences/${seq.sequence_id}/monitor`)} data-testid={`monitor-seq-${seq.sequence_id}`}>
                                        <Activity className="w-3.5 h-3.5 mr-1.5" />Monitor
                                    </Button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Create Modal */}
            <Dialog open={showCreate} onOpenChange={setShowCreate}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>New Sequence</DialogTitle>
                        <DialogDescription>Create a multi-contact email sequence for an account</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-3">
                        <div>
                            <Label>Sequence Name *</Label>
                            <Input value={createForm.name} onChange={e => setCreateForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g., TechVenture Executive Outreach" className="mt-1" data-testid="seq-name-input" />
                        </div>
                        <div>
                            <Label>Account *</Label>
                            <Select value={createForm.account_id} onValueChange={v => setCreateForm(p => ({ ...p, account_id: v }))}>
                                <SelectTrigger className="mt-1" data-testid="seq-account-select">
                                    <SelectValue placeholder="Select an account" />
                                </SelectTrigger>
                                <SelectContent>
                                    {accounts.map(a => <SelectItem key={a.account_id} value={a.account_id}>{a.name}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
                        <Button onClick={handleCreate} className="bg-stone-900 hover:bg-stone-800" data-testid="create-seq-btn">Create Sequence</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
